import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, MessageCircle, X } from "lucide-react";
import { useLang } from "./LangContext";
import { useSettings, whatsappBookingLink } from "./SettingsContext";
import { EASE } from "./Reveal";

export default function WhatsAppFab() {
  const { t } = useLang();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const b = t.branches;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const options = b.items
    .map((branch) => ({ branch, href: whatsappBookingLink(branch, settings?.contact, b.waMessage) }))
    .filter((o) => o.href);
  if (!options.length) return null;

  return (
    <div className="wa-fab-root" data-testid="whatsapp-fab-root">
      <AnimatePresence>
        {open && (
          <motion.div
            className="wa-panel"
            role="dialog"
            aria-label={b.waFab.title}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.35, ease: EASE }}
            data-testid="whatsapp-fab-panel"
          >
            <div className="wa-panel-head">
              <div>
                <p className="wa-panel-title font-display">{b.waFab.title}</p>
                <p className="wa-panel-lead">{b.waFab.lead}</p>
              </div>
              <button className="wa-panel-close" onClick={() => setOpen(false)} aria-label={b.waFab.close} data-testid="whatsapp-fab-close">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="wa-panel-list">
              {options.map(({ branch, href }) => (
                <a key={branch.id} className="wa-branch" href={href} target="_blank" rel="noopener noreferrer"
                  onClick={() => setOpen(false)} data-testid={`whatsapp-fab-branch-${branch.id}`}>
                  <MessageCircle size={18} aria-hidden="true" />
                  <span className="wa-branch-text">
                    <span className="wa-branch-city">{branch.city}</span>
                    <span className="wa-branch-name">{branch.name}</span>
                  </span>
                  <ChevronRight size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        className={`wa-fab${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={b.waFab.label}
        data-testid="whatsapp-fab-button"
      >
        <span className="wa-fab-pulse" aria-hidden="true" />
        {open ? <X size={24} aria-hidden="true" /> : <MessageCircle size={26} aria-hidden="true" />}
        <span className="wa-fab-label">{b.waFab.label}</span>
      </button>
    </div>
  );
}
